import { authenticate } from "@/controllers/auth";
import { getUserUniverses } from "@/controllers/universes";
import { customViewsState, subscriptionOfferingsState, universesState, userDataState, userPayloadSettledState, userState } from "@/lib/store";
import { planUserCallFailure } from "@/lib/userCallFailure";
import { isOrtexApp } from "@/lib/utils";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import { v4 as uuidv4 } from "uuid";
import useOpenSubscriptionForm from "./useOpenSubscriptionForm";
import useAppSubscriberUpgrade from "./useAppSubscriberUpgrade";
import axios from "axios";
import { installVisitorGate, noteUser } from "@/lib/visitorGate";

// Members-only calls are answered locally for visitors (src/lib/visitorGate.ts). Installed when
// _app loads this hook, before any page sends a request; the user call below tells it who is who.
if (typeof window !== "undefined") installVisitorGate(axios);

const useGetUserData = (isAuthenticated = true) => {
    const { pathname, replace } = useRouter();
    const setUniverses = useSetAtom(universesState);
    const setUser = useSetAtom(userState);
    const setUserSettled = useSetAtom(userPayloadSettledState);
    const [loading, setLoading] = useState(true);
    const setCustomViews = useSetAtom(customViewsState);
    const offerings = useAtomValue(subscriptionOfferingsState);
    const { openSubscriptionForm } = useOpenSubscriptionForm();
    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();
    const store = useStore();
    // Transient failures (429, 5xx, network) are retried with backoff: see src/lib/userCallFailure.ts.
    const retryAttempt = useRef(0);
    const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The run the current effect owns; a run left over from an earlier effect never schedules a retry.
    const activeRun = useRef<(() => Promise<void>) | null>(null);
    // A retry runs after the reader may have moved on: judge the failure against the page they are on now.
    const currentPathname = useRef(pathname);
    currentPathname.current = pathname;

    const fetchData = useMemo(() => {
        if (!isAuthenticated) return;

        const run = async () => {
            let user: Awaited<ReturnType<typeof authenticate>>;
            let universes: Awaited<ReturnType<typeof getUserUniverses>>;
            try {
                [user, universes] = await Promise.all([authenticate(), getUserUniverses()]);
            } catch (error) {
                // Only a definite 401/403 is "not signed in", and only a signed-in-only page sends
                // that visitor to /login. A 429, 5xx or network error keeps the reader on the page
                // (a member stays signed in) and retries (#1348).
                const plan = planUserCallFailure({ error, pathname: currentPathname.current, hasUser: !!store.get(userDataState), attempt: retryAttempt.current });
                if (plan.clearUser) setUser(null);
                // A definite 401/403 (no retry) is an answer: there is no user. A transient failure is not.
                if (plan.clearUser && plan.retryInMs === null) {
                    setUserSettled(true);
                    noteUser(null);
                }
                if (plan.clearAmplifyKeys) {
                    Object.keys(localStorage).forEach((key) => {
                        if (key.startsWith("CognitoIdentityServiceProvider") || key.startsWith("amplify-")) {
                            localStorage.removeItem(key);
                        }
                    });
                }
                if (plan.stopLoading) setLoading(false);
                if (plan.redirectToLogin) replace("/login");
                if (plan.retryInMs !== null && activeRun.current === run) {
                    retryAttempt.current += 1;
                    retryTimer.current = setTimeout(() => {
                        retryTimer.current = null;
                        run();
                    }, plan.retryInMs);
                }
                return;
            }
            retryAttempt.current = 0;
            noteUser(user);
            handleAppSubscriberUpgrade({
                openPaymentModal: false,
                openForm: false,
                closeModalAfterDelay: false,
                user,
            });
            // For overdue users we open the subscription form so they
            // can renew. App subscribers are *already* having their
            // subscription bootstrapped via `handleAppSubscriberUpgrade`
            // (which fires `restartSubscription("APPDISC")` on their
            // behalf) — calling `openSubscriptionForm()` here would
            // double-fire `/API/v2/user/new_subscription`, once with
            // APPDISC and once without, both of which take ~3s on the
            // backend. So skip the no-code call for app subscribers.
            if (user.overdue && !user.app_subscriber_only && (!isOrtexApp() || offerings)) {
                openSubscriptionForm();
            }

            setUniverses(universes);
            setUser(user);
            setUserSettled(true);

            if (user.settings?.module_settings) {
                if (user.settings.module_settings["custom_views->tabs"] && user.settings.module_settings["custom_views->tabs"].length > 0) {
                    setCustomViews(user.settings.module_settings["custom_views->tabs"]);
                } else {
                    setCustomViews([{ id: uuidv4(), name: "Default Custom View", layout: [], mobileLayout: [], modules: [], lastUpdated: dayjs().format("DD MMM YY") }]);
                }
            }

            if (["/login", "/login-legacy", "/cognito-login"].includes(currentPathname.current) && user.email !== "visitor@ortex.com") {
                // Only redirect home if Amplify also has a session. If the Django cookie
                // is valid but Amplify tokens are gone, it's a stale cookie from a failed
                // backend logout — show the login form instead of bouncing back.
                // Mobile app authenticates via backend session only (no Amplify tokens),
                // so skip the Amplify check there — unless the user just explicitly logged out.
                const hasAmplifySession = (isOrtexApp() && !!localStorage.getItem("ortexUser")) || Object.keys(localStorage).some((key) => key.startsWith("CognitoIdentityServiceProvider") && key.endsWith(".accessToken"));
                if (hasAmplifySession) {
                    // Carry affiliate promo/checkout params through to the payment
                    // page so an already-logged-in user who lands on an affiliate
                    // /login link still gets the discounted upgrade flow instead of
                    // being dropped on the home page (params lost).
                    const sp = new URLSearchParams(window.location.search);
                    const parts = ["promo", "t", "v", "checkout"].filter((k) => sp.get(k)).map((k) => `${k}=${encodeURIComponent(sp.get(k) as string)}`);
                    replace(parts.length ? `/upgrade?${parts.join("&")}` : "/");
                } else {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };
        return run;
    }, [isAuthenticated, isOrtexApp(), offerings]);

    useEffect(() => {
        if (!fetchData) return;
        retryAttempt.current = 0;
        activeRun.current = fetchData;
        fetchData();
        return () => {
            activeRun.current = null;
            if (retryTimer.current) clearTimeout(retryTimer.current);
            retryTimer.current = null;
        };
    }, [fetchData]);

    return { userLoading: loading };
};

export default useGetUserData;
